// Chart color system for the leave dashboard (light surface, dataviz-validated).
// Categorical hues are assigned per leave variation and NEVER re-shuffle when
// filters change; status colors are reserved for state and never used as series.

// Canonical leave-type hues (mockup parity): annual=blue, sick=amber,
// maternity/paternity=violet, unpaid=gray. Matching is by name keyword because
// LeaveVariation has no type field. The gray is a semantic NEUTRAL (unpaid),
// not a series slot — the validated categorical set is the 7 chromatic hues
// (validate_palette.js, light surface #FFFFFF: PASS; sub-3:1 hues rely on the
// ever-present chip text/legend relief).
const CANONICAL_TYPES = [
  { keywords: ['yıllık', 'yillik', 'annual'], color: '#2a78d6' },
  { keywords: ['hasta', 'rapor', 'sick'], color: '#eda100' },
  { keywords: ['babalık', 'babalik', 'annelik', 'doğum', 'dogum', 'maternity', 'paternity', 'ebeveyn'], color: '#4a3aa7' },
  { keywords: ['ücretsiz', 'ucretsiz', 'unpaid'], color: '#898781' },
];

// Extra fixed slots for tenant-specific types, assigned in stable (tr-sorted) order.
const EXTRA_SLOTS = ['#1baf7a', '#e87ba4', '#eb6834', '#008300'];

// Fold color once every slot is taken (kept lighter than the "unpaid" gray).
export const TYPE_FOLD_COLOR = '#b3b1ab';

// Builds a stable Map<variationId, color> from every variation visible in the
// current payloads. Sort order (not arrival order) drives extra-slot assignment
// so the same tenant always gets the same colors.
export function buildLeaveTypeColorMap(variations) {
  const map = new Map();
  const sorted = [...variations]
    .filter((v) => v && v.id)
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));

  sorted.forEach((v) => {
    if (map.has(v.id)) return;
    const lower = (v.name || '').toLocaleLowerCase('tr-TR');
    const hit = CANONICAL_TYPES.find((c) => c.keywords.some((k) => lower.includes(k)));
    if (hit) map.set(v.id, hit.color);
  });

  let slot = 0;
  sorted.forEach((v) => {
    if (map.has(v.id)) return;
    map.set(v.id, slot < EXTRA_SLOTS.length ? EXTRA_SLOTS[slot] : TYPE_FOLD_COLOR);
    slot += 1;
  });

  return map;
}

// Single-hue blue ramp for the density heatmap (light→dark, 5 discrete steps).
// Starts at the ordinal-safe step so the lightest cell still separates from
// empty-day cells (validated: light end 2.11:1 on white, monotone L).
export const HEAT_RAMP = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#0d366b'];
export const HEAT_EMPTY = '#EEF1F5';

export function heatColor(count, maxCount) {
  if (!count || count <= 0) return HEAT_EMPTY;
  const max = Math.max(1, maxCount || 1);
  const idx = Math.min(HEAT_RAMP.length - 1, Math.floor(((count - 1) / max) * HEAT_RAMP.length));
  return HEAT_RAMP[idx];
}

// Status palette (reserved for state; always paired with a printed % or icon).
export const STATUS = {
  good: '#02D3BF',
  normal: '#0291EF',
  warning: '#FFC107',
  critical: '#E53935',
};

// Capacity bands measured on the mockup: ≥85 green, 70-84 blue, 60-69 amber, <60 red.
export function capacityBand(pct) {
  if (pct >= 85) return { key: 'high', color: STATUS.good };
  if (pct >= 70) return { key: 'normal', color: STATUS.normal };
  if (pct >= 60) return { key: 'watch', color: STATUS.warning };
  return { key: 'critical', color: STATUS.critical };
}

// Burnout score bands (score is always printed next to the bar).
export function burnoutColor(score) {
  if (score >= 80) return STATUS.critical;
  if (score >= 60) return STATUS.warning;
  return STATUS.normal;
}

export const KPI_ACCENTS = {
  onLeave: '#2a78d6',
  queue: '#eda100',
  upcoming: '#1baf7a',
  risk: '#E53935',
  expiring: '#FFC107',
};

export const SEVERITY_COLORS = {
  critical: '#E53935',
  warning: '#eda100',
  info: '#898781',
};
